import React, { useState, useEffect } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Box,
  Typography,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Card,
  CardContent,
  Stack
} from "@mui/material";
import {
  IoCloudUpload,
  IoCheckmarkCircle,
  IoStopCircle
} from "react-icons/io5";
import { getData } from "../../Webservice/Webservice";

const BannerModal = ({
  open,
  handleClose,
  handleSubmit,
  isEdit,
  editingBanner
}) => {
  const [bannerData, setBannerData] = useState(
    isEdit && editingBanner
      ? editingBanner
      : { image: null, type: "", link: "", status: "active", position: 1 }
  );
  const [subcategories, setSubcategories] = useState([]);
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  // Fetch subcategories or products based on Banner Type
  useEffect(
    () => {
      const fetchData = async () => {
        setLoading(true);
        try {
          const tokenValue = sessionStorage.getItem("token");
          const token = tokenValue ? `Bearer ${tokenValue}` : "";
          if (bannerData.type === "subcategory") {
            const response = await getData("subcategories/list?limit=1000", null, token);
            setSubcategories(response.subcategories || []); // Updated to use response.subcategories
            setProducts([]);
            setError(null);
          } else if (bannerData.type === "product") {
            const response = await getData(
              "product/list?isFree=false&limit=1000",
              null,
              token
            );
            setProducts(response.products || []);
            setSubcategories([]);
            setError(null);
          } else {
            setSubcategories([]);
            setProducts([]);
            setError(null);
          }
        } catch (err) {
          setError("Failed to fetch data");
          setSubcategories([]);
          setProducts([]);
        } finally {
          setLoading(false);
        }
      };
      fetchData();
    },
    [bannerData.type]
  );

  // Reset bannerData when isEdit or editingBanner changes
  useEffect(
    () => {
      if (isEdit && editingBanner) {
        setBannerData(editingBanner);
      } else {
        setBannerData({
          image: null,
          type: "",
          link: "",
          status: "active",
          position: 1
        });
      }
    },
    [isEdit, editingBanner]
  );

  const handleChange = e => {
    const { name, value } = e.target;
    setBannerData({ ...bannerData, [name]: value });
  };

  const handleImageChange = e => {
    const file = e.target.files[0];
    if (file) {
      setBannerData({
        ...bannerData,
        image: file,
        imagePreview: URL.createObjectURL(file)
      });
    }
  };

  const onSubmit = e => {
    e.preventDefault();
    handleSubmit(bannerData);
  };

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth="sm"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: "12px",
          boxShadow:
            "0 24px 56px rgba(0, 0, 0, 0.3), 0 8px 24px rgba(0, 0, 0, 0.25)",
          border: "1px solid rgba(226, 232, 240, 0.5)",
          overflow: "hidden"
        }
      }}
    >
      <DialogTitle
        sx={{
          color: "white",
          p: 0,
          position: "relative",
          overflow: "hidden"
        }}
      >
        <Box
          sx={{
            position: "absolute",
            top: 0,
            right: 0,
            bottom: 0,
            left: 0
          }}
        />
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            p: 2.5,
            position: "relative"
          }}
        >
          <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
            <Box>
              <Typography
                variant="h5"
                sx={{
                  fontWeight: 700,
                  fontSize: "1.5rem",
                  color: "black",
                  pl: "12px",
                  fontFamily: '"mulish",san-serif'
                }}
              >
                {isEdit ? "Edit Banner" : "Create New Banner"}
              </Typography>
            </Box>
          </Box>
        </Box>
      </DialogTitle>

      <form onSubmit={onSubmit}>
        <DialogContent sx={{ p: 0, backgroundColor: "transparent" }}>
          <Box sx={{ padding: "0px 25px" }}>
            <Stack spacing={2}>
              <Card elevation={0}>
                <CardContent sx={{ p: 2 }}>
                  <Box
                    sx={{
                      border: "2px dashed #cbd5e1",
                      borderRadius: "16px",
                      p: 2,
                      textAlign: "center",
                      backgroundColor: "#f8fafc",
                      transition: "all 0.3s ease",
                      cursor: "pointer",
                      position: "relative",
                      "&:hover": {
                        borderColor: "rgb(255 209 2 /50%)",
                        backgroundColor: "rgb(255 209 2 /5%)"
                      }
                    }}
                  >
                    <input
                      type="file"
                      accept="image/*"
                      onChange={handleImageChange}
                      required={!isEdit}
                      style={{
                        position: "absolute",
                        top: 0,
                        left: 0,
                        right: 0,
                        bottom: 0,
                        opacity: 0,
                        cursor: "pointer"
                      }}
                    />
                    {bannerData.imagePreview ||
                    (bannerData.image && typeof bannerData.image === "string")
                      ? <Box>
                          <img
                            src={bannerData.imagePreview || bannerData.image}
                            alt="Banner preview"
                            style={{
                              maxWidth: "100%",
                              maxHeight: "200px",
                              objectFit: "contain",
                              borderRadius: "12px",
                              boxShadow: "0 8px 24px rgba(0, 0, 0, 0.12)"
                            }}
                          />
                        </Box>
                      : <Box>
                          <Box
                            sx={{
                              width: 64,
                              height: 64,
                              borderRadius: "12px",
                              background: "rgb(255 209 2)",
                              display: "flex",
                              alignItems: "center",
                              justifyContent: "center",
                              mx: "auto",
                              mb: 2,
                              boxShadow: "0 4px 12px rgba(59, 130, 246, 0.3)"
                            }}
                          >
                            <IoCloudUpload
                              style={{ color: "white", fontSize: "32px" }}
                            />
                          </Box>
                          <Typography
                            variant="h6"
                            sx={{
                              color: "#1e293b",
                              fontWeight: 600,
                              mb: 1,
                              fontFamily: '"mulish",san-serif'
                            }}
                          >
                            Upload Banner Image
                          </Typography>
                          <Typography
                            variant="body2"
                            sx={{
                              color: "#64748b",
                              fontFamily: '"mulish", san-serif'
                            }}
                          >
                            Click to browse or drag and drop your image here
                          </Typography>
                        </Box>}
                  </Box>

                  <CardContent sx={{ padding: "15px 1px" }}>
                    <Stack spacing={1.5}>
                      <Stack
                        direction={{ xs: "column", sm: "row" }}
                        spacing={2}
                      >
                        <FormControl fullWidth>
                          <InputLabel
                            sx={{
                              color: "#64748b",
                              "&.Mui-focused": { color: "#3b82f6" }
                            }}
                          >
                            Banner Type
                          </InputLabel>
                          <Select
                            name="type"
                            value={bannerData.type}
                            onChange={handleChange}
                            label="Banner Type"
                            sx={{
                              borderRadius: "12px",
                              "& .MuiOutlinedInput-notchedOutline": {
                                borderColor: "#e2e8f0"
                              },
                              "&:hover .MuiOutlinedInput-notchedOutline": {
                                borderColor: "#3b82f6"
                              },
                              "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
                                borderColor: "#3b82f6",
                                borderWidth: "2px"
                              }
                            }}
                          >
                            <MenuItem
                              value="subcategory"
                              sx={{
                                display: "flex",
                                alignItems: "center",
                                gap: 1
                              }}
                            >
                              Sub Category
                            </MenuItem>
                            <MenuItem
                              value="product"
                              sx={{
                                display: "flex",
                                alignItems: "center",
                                gap: 1
                              }}
                            >
                              Product
                            </MenuItem>
                            <MenuItem
                              value="external"
                              sx={{
                                display: "flex",
                                alignItems: "center",
                                gap: 1
                              }}
                            >
                              External
                            </MenuItem>
                          </Select>
                        </FormControl>

                        {bannerData.type === "subcategory"
                          ? <FormControl fullWidth>
                              <InputLabel
                                sx={{
                                  color: "#64748b",
                                  "&.Mui-focused": { color: "#3b82f6" }
                                }}
                              >
                                Banner Link (subcategory)
                              </InputLabel>
                              <Select
                                name="link"
                                value={bannerData.link}
                                onChange={handleChange}
                                label="Banner Link (subcategory)"
                                disabled={loading || !!error}
                                sx={{
                                  borderRadius: "12px",
                                  "& .MuiOutlinedInput-notchedOutline": {
                                    borderColor: "#e2e8f0"
                                  },
                                  "&:hover .MuiOutlinedInput-notchedOutline": {
                                    borderColor: "#3b82f6"
                                  },
                                  "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
                                    borderColor: "#3b82f6",
                                    borderWidth: "2px"
                                  }
                                }}
                              >
                                {loading
                                  ? <MenuItem disabled>Loading...</MenuItem>
                                  : error
                                    ? <MenuItem disabled>
                                        {error}
                                      </MenuItem>
                                    : subcategories.length > 0
                                      ? subcategories.map(subcategory =>
                                          <MenuItem
                                            key={subcategory._id}
                                            value={subcategory._id}
                                          >
                                            {subcategory.title}{" "}
                                            {/* Updated to use subCategory.title */}
                                          </MenuItem>
                                        )
                                      : <MenuItem disabled>
                                          No subcategories available
                                        </MenuItem>}
                              </Select>
                            </FormControl>
                          : bannerData.type === "product"
                            ? <FormControl fullWidth>
                                <InputLabel
                                  sx={{
                                    color: "#64748b",
                                    "&.Mui-focused": { color: "#3b82f6" }
                                  }}
                                >
                                  Banner Link (Product)
                                </InputLabel>
                                <Select
                                  name="link"
                                  value={bannerData.link}
                                  onChange={handleChange}
                                  label="Banner Link (Product)"
                                  disabled={loading || !!error}
                                  sx={{
                                    borderRadius: "12px",
                                    "& .MuiOutlinedInput-notchedOutline": {
                                      borderColor: "#e2e8f0"
                                    },
                                    "&:hover .MuiOutlinedInput-notchedOutline": {
                                      borderColor: "#3b82f6"
                                    },
                                    "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
                                      borderColor: "#3b82f6",
                                      borderWidth: "2px"
                                    }
                                  }}
                                >
                                  {loading
                                    ? <MenuItem disabled>Loading...</MenuItem>
                                    : error
                                      ? <MenuItem disabled>
                                          {error}
                                        </MenuItem>
                                      : products.length > 0
                                        ? products.map(product =>
                                            <MenuItem
                                              key={product._id}
                                              value={product._id}
                                            >
                                              {product.title}
                                            </MenuItem>
                                          )
                                        : <MenuItem disabled>
                                            No products available
                                          </MenuItem>}
                                </Select>
                              </FormControl>
                            : <TextField
                                fullWidth
                                label="Banner Link"
                                name="link"
                                value={bannerData.link}
                                onChange={handleChange}
                                InputProps={{
                                  sx: {
                                    borderRadius: "12px",
                                    "& .MuiOutlinedInput-notchedOutline": {
                                      borderColor: "#e2e8f0"
                                    },
                                    "&:hover .MuiOutlinedInput-notchedOutline": {
                                      borderColor: "#3b82f6"
                                    },
                                    "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
                                      borderColor: "#3b82f6",
                                      borderWidth: "2px"
                                    }
                                  }
                                }}
                                InputLabelProps={{
                                  sx: {
                                    color: "#64748b",
                                    "&.Mui-focused": { color: "#3b82f6" }
                                  }
                                }}
                              />}
                      </Stack>
                      <Stack
                        direction={{ xs: "column", sm: "row" }}
                        spacing={2}
                      >
                        <TextField
                          fullWidth
                          label="Display Position"
                          name="position"
                          type="number"
                          value={bannerData.position}
                          onChange={handleChange}
                          required
                          InputProps={{
                            inputProps: { min: 1 },
                            sx: {
                              borderRadius: "12px",
                              "& .MuiOutlinedInput-notchedOutline": {
                                borderColor: "#e2e8f0"
                              },
                              "&:hover .MuiOutlinedInput-notchedOutline": {
                                borderColor: "#3b82f6"
                              },
                              "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
                                borderColor: "#3b82f6",
                                borderWidth: "2px"
                              }
                            }
                          }}
                          InputLabelProps={{
                            sx: {
                              color: "#64748b",
                              "&.Mui-focused": { color: "#3b82f6" }
                            }
                          }}
                        />
                        <FormControl fullWidth>
                          <InputLabel
                            sx={{
                              color: "#64748b",
                              "&.Mui-focused": { color: "#3b82f6" }
                            }}
                          >
                            Status
                          </InputLabel>
                          <Select
                            name="status"
                            value={bannerData.status}
                            onChange={handleChange}
                            label="Status"
                            sx={{
                              borderRadius: "12px",
                              "& .MuiOutlinedInput-notchedOutline": {
                                borderColor: "#e2e8f0"
                              },
                              "&:hover .MuiOutlinedInput-notchedOutline": {
                                borderColor: "#3b82f6"
                              },
                              "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
                                borderColor: "#3b82f6",
                                borderWidth: "2px"
                              }
                            }}
                          >
                            <MenuItem value="active">
                              <Box
                                sx={{
                                  display: "flex",
                                  alignItems: "center",
                                  gap: 1
                                }}
                              >
                                <IoCheckmarkCircle
                                  style={{ color: "#10b981", fontSize: "16px" }}
                                />
                                <span>Active</span>
                              </Box>
                            </MenuItem>
                            <MenuItem value="inactive">
                              <Box
                                sx={{
                                  display: "flex",
                                  alignItems: "center",
                                  gap: 1
                                }}
                              >
                                <IoStopCircle
                                  style={{ color: "#6b7280", fontSize: "16px" }}
                                />
                                <span>Inactive</span>
                              </Box>
                            </MenuItem>
                          </Select>
                        </FormControl>
                      </Stack>
                    </Stack>
                  </CardContent>
                </CardContent>
              </Card>
            </Stack>
          </Box>
        </DialogContent>

        <DialogActions
          sx={{
            padding: "0px 30px 15px 30px",
            gap: 2,
            justifyContent: "flex-end"
          }}
        >
          <Button
            onClick={handleClose}
            sx={{
              color: "red",
              borderColor: "red",
              fontWeight: 500,
              px: 3,
              py: 1,
              borderRadius: "8px",
              textTransform: "none",
              border: "1px solid red",
              backgroundColor: "white",
              "&:hover": {
                backgroundColor: "#f8fafc",
                borderColor: "red"
              }
            }}
          >
            Cancel
          </Button>
          <Button
            type="submit"
            variant="contained"
            sx={{
              color: "black",
              border: "1px solid black",
              backgroundColor: "rgb(255 209 2)",
              fontWeight: 600,
              px: 4,
              py: 1,
              borderRadius: "8px",
              "&:hover": {
                background: "rgb(255 209 2)"
              }
            }}
          >
            {isEdit ? "Update" : "Create"}
          </Button>
        </DialogActions>
      </form>
    </Dialog>
  );
};

export default BannerModal;
